"use client";

import { useDeferredValue, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  offlineDb,
  useLiveQuery,
  useOnDemandWindow,
  type OnDemandWindow,
} from "@stockline/web/offline";
import type { PreOrder as OfflinePreOrder } from "@stockline/shared/types/orders/pre-order";
import type { PreOrderFlow as OfflinePreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow";
import type {
  PreOrder as LegacyPreOrder,
  PreOrderFlow as LegacyPreOrderFlow,
  PreOrderLinkedEmail,
  PreOrderMetadata,
} from "@/app/api/types/preOrder";
import type { Email } from "@stockline/shared/types/communications/email";
import {
  computeUTCRangeInTz,
  useCompanyTimezone,
} from "@/lib/datetime/companyTimezone";
import { enrichRecapPreOrders } from "./cacheHelpers";
import { mergeMessageIdsIntoMetadata } from "@/lib/v2-api/merge-message-ids";
import { perfMark, perfMeasure } from "@/app/recap/utils/perfLogger";
import { useUser } from "@/components/auth-provider";
import { format } from "date-fns";

// Upper-bound sentinel for compound-index range queries on date strings.
// IndexedDB compares compound keys lexicographically, so any string beyond
// the maximum BMP code point sorts after every ISO date we'd ever index.
export const DATE_UPPER_BOUND = "￿";

// Hard cap on the number of pre-orders the recap will ever pull from Dexie
// in one snapshot. Protects against open-ended date ranges (rangeMode
// "starting_from") on tenants with thousands of historical POs — without
// it, a single broad query can drag 10k+ POs and ~50k+ flows into JS
// memory, then through the per-PO mapping / enrichment / per-row React
// render pipeline, freezing the main thread to the point the tab is hard
// to close. We always fetch CAP+1 and trim back to CAP so we can flag
// truncation to consumers without paying for an extra .count() roundtrip.
export const RECAP_PO_HARD_CAP = 2000;

// HOT offline window (STK-1322/1323): the device keeps only orders from the
// last N days (bounded by the server windowFloor). A recap range entirely
// older than this is below the floor → fetched on demand. MUST match the sync
// engine's `window.days` (apps/web pre-order config) + `windowStartFor`.
const HOT_WINDOW_DAYS = 30;

// Resource table names. Hard-coded to avoid a dependency on `@stockline/web`'s
// internal subpath barrel — the table names are part of the offline contract.
const PRE_ORDER_TABLE = "preOrders";
const PRE_ORDER_FLOW_TABLE = "preOrderFlows";
const PRE_ORDER_MESSAGE_TABLE = "preOrderMessages";
const PRE_ORDER_SMS_MESSAGE_TABLE = "preOrderSmsMessages";
const PRE_ORDER_EMAIL_TABLE = "preOrderEmails";
// Source-of-truth table for email row bodies (subject, from, message_id,
// user_id). The `preOrderEmails` table above is only the PO↔email junction.
const EMAIL_TABLE = "emails";

interface OfflinePreOrderMessage {
  id: string;
  preOrderId: string | null;
  messageId: string | null;
  chatId: string | null;
  createdAt?: string;
  deletedAt?: string | null;
}
interface OfflinePreOrderSmsMessage {
  id: string;
  preOrderId: string | null;
  messageId: string | null;
  smsChatId: string | null;
  createdAt?: string;
  deletedAt?: string | null;
}
interface OfflinePreOrderEmail {
  id: string;
  preOrderId: string | null;
  emailId: string | null;
  deletedAt?: string | null;
}

/**
 * Read all rows of a junction `table` whose `preOrderId` is in `ids`.
 *
 * Replaces `.where("preOrderId").anyOf(ids).toArray()`. Dexie's `anyOf` is
 * CPU-bound JS that scales super-linearly under CPU throttle — on real client
 * hardware (6× slowdown) the 4-way junction fetch was the dominant recap-load
 * cost (~1.3 s for the flow table alone on a busy day). Issuing one
 * `.equals(id)` per id inside a single read transaction does the same index
 * walk far more cheaply: ~158 ms → ~38 ms at 1×, ~1346 ms → ~262 ms at 6×
 * (~5×), measured on real data. Crucially it stays inside Dexie's observable
 * layer, so `useLiveQuery` keeps re-running on junction writes (a raw
 * IndexedDB read would be marginally faster but invisible to liveQuery).
 * See `docs/perf/LOCAL_DB_BENCHMARK.md`.
 *
 * Exported for the parity test against the previous `anyOf` behavior.
 */
export async function fetchJunctionByPreOrderIds<T>(
  table: string,
  ids: readonly string[],
): Promise<T[]> {
  if (ids.length === 0) return [];
  return offlineDb.transaction("r", offlineDb.table(table), async () => {
    const parts = await Promise.all(
      ids.map(
        (id) =>
          offlineDb
            .table<T>(table)
            .where("preOrderId")
            .equals(id)
            .toArray() as Promise<T[]>,
      ),
    );
    return parts.flat();
  });
}

/**
 * Pre-order shard of the recap slice — driven by writes to the `preOrders`
 * table only. Cheap to recompute on a header field edit because it doesn't
 * read any junction tables.
 */
interface PreOrderSlice {
  preOrders: OfflinePreOrder[];
  /**
   * Deps signature this slice was produced under. Compared at render to the
   * current deps to detect "deps changed but useLiveQuery hasn't re-resolved
   * yet" — that's the window where we want to show the loading state instead
   * of leaving the previous date's pre-orders on screen.
   */
  depsKey: string;
  /**
   * True when the Dexie query for the active date scope returned more than
   * `RECAP_PO_HARD_CAP` rows and we kept only the oldest CAP. Surfaced to
   * the consumer so the recap can show a banner telling the user to narrow
   * the start date forward to see more recent activity.
   */
  truncated: boolean;
}

/**
 * Junction shard — driven by writes to the junction tables (and the `emails`
 * table for the per-email metadata join). Independent of header field edits
 * on the pre-orders themselves, which is the whole point of the split:
 * editing `delivery_date` doesn't re-fire this and doesn't pay the 1 s
 * junction-fetch cost.
 *
 * `forIdsKey` records the sorted pre-order id signature this shard was
 * computed against; the combine step uses it to detect a "junctions still
 * lagging the new pre-order set" window after a date-range change.
 */
interface JunctionSlice {
  flows: OfflinePreOrderFlow[];
  messages: OfflinePreOrderMessage[];
  smsMessages: OfflinePreOrderSmsMessage[];
  emails: OfflinePreOrderEmail[];
  /**
   * Per-email-id metadata pulled from the `emails` Dexie table for every email
   * row referenced by any visible PO's junctions. Drives `linked_emails[]` on
   * the mapped pre-order so downstream code (`useLinkedSources`) can group
   * sibling rows by `message_id` and pick the entry owned by the current user.
   * Joined lazily inside the live-query callback so it stays reactive to email
   * row writes (e.g. message_id backfill after initial sync).
   *
   * Presence in this map is also the **visibility signal**: a junction id that
   * doesn't resolve to a Dexie row (cross-user dedup sibling that the viewer
   * can't read per `emailVisibilityFilter`) gets filtered out of `linked_emails`
   * and `linked_email_ids` downstream. `message_id` is optional because a row
   * may be in Dexie before the backfill stamps its RFC Message-Id — those rows
   * still count as visible (the user can open them) but they can't participate
   * in sibling-collapse until the backfill lands.
   */
  linkedEmailMetaById: Map<string, { user_id: string; message_id?: string }>;
  forIdsKey: string;
}

/**
 * Combined slice — same shape downstream consumers (`mapped`, the cache key
 * builder, etc.) have always seen. Built by merging `PreOrderSlice` +
 * `JunctionSlice` in a `useMemo` so the split is transparent below the hook.
 */
interface RecapSlice extends PreOrderSlice, Omit<JunctionSlice, "forIdsKey"> {
  /**
   * True while the junction shard still reflects a previous pre-order id
   * set — i.e. the pre-orders in this slice are current but their
   * flows/linked-source rows are still being fetched (progressive paint
   * window after a date-scope change). Consumers render the PO headers
   * immediately and show a placeholder where the flow rows will land.
   */
  junctionsPending: boolean;
}

const EMPTY_PRE_ORDER_SLICE: PreOrderSlice = {
  preOrders: [],
  depsKey: "",
  truncated: false,
};

const EMPTY_JUNCTION_SLICE: JunctionSlice = {
  flows: [],
  messages: [],
  smsMessages: [],
  emails: [],
  linkedEmailMetaById: new Map(),
  forIdsKey: "",
};

const EMPTY_SLICE: RecapSlice = {
  preOrders: [],
  flows: [],
  messages: [],
  smsMessages: [],
  emails: [],
  linkedEmailMetaById: new Map(),
  depsKey: "",
  truncated: false,
  junctionsPending: false,
};

// ---------------------------------------------------------------------------
// Module-level slice caches — survive component remounts.
//
// Why: `useOfflineRecapPreOrders` lives on `RecapShell`, which unmounts when
// the user navigates from /recap to /recap/:id and remounts when they go
// back. Each remount re-arms `useLiveQuery`, which kicks off a fresh
// fetchScopedPreOrders + fetchJunctions roundtrip (~1 s combined on a
// throttled CPU) and leaves the user staring at "Chargement des commandes..."
// for what should be an instant restore of the previous view.
//
// These two caches let the remount pick up the *previous resolved value*
// synchronously as the initial value of `useLiveQuery`. Dexie still re-runs
// the live query in the background to validate, but the user sees the list
// the moment the route renders — `isStale` already gates downstream
// freshness checks so a brief flicker between "cached" and "fresh" is
// indistinguishable when the underlying data hasn't changed.
//
// Keyed by:
//   - preOrderShardCache:   depsKey (companyId|userId|dateField|dateStr|range|…)
//   - junctionShardCache:   `${userId}|${preOrderIdsKey}|${preOrderEmailIdsKey}|${includeDeleted}`
//
// Both keys include the current user id so an in-tab user switch can't
// replay another user's visibility-scoped data (notably the linked-email
// metadata stitched onto junction rows, which is filtered per viewer).
//
// Eviction: hard cap of MAX_CACHE_ENTRIES per cache. When exceeded, the
// oldest entry (insertion order via Map) is dropped — same shape as a
// minimal LRU keyed on touch order, sufficient for the handful of unique
// date / id-set combinations a single session encounters.
// ---------------------------------------------------------------------------
const MAX_CACHE_ENTRIES = 24;
const preOrderShardCache = new Map<string, PreOrderSlice>();
const junctionShardCache = new Map<string, JunctionSlice>();

function rememberShard<T>(cache: Map<string, T>, key: string, value: T): void {
  // LRU-touch: delete + set so the entry moves to the tail of insertion order.
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  if (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
}

/**
 * Recap data hook backed by the offline-first Dexie store.
 *
 * Uses Dexie's IndexedDB indexes to fetch ONLY the rows in the active date
 * scope rather than dumping the full company tables into JS and filtering
 * in memory. The schema-side indexes added in DB v30 (`deliveryDate`,
 * `orderDate`, plus `[companyId+deliveryDate]` / `[companyId+orderDate]`
 * compounds) make the date scan an O(K) index walk where K = visible POs
 * (~5–55 on a typical day) instead of O(N) over the full table (~20k+).
 *
 * Pipeline:
 * 1. Query `preOrders` by indexed `deliveryDate` / `orderDate`.
 * 2. Take the visible PO ids and query `preOrderFlows` /
 *    `preOrderMessages` / `preOrderSmsMessages` / `preOrderEmails` scoped to
 *    those ids only — using the existing `preOrderId` indexes on each
 *    junction table. No more 58k-flow scan to find the ~150 we care about.
 * 3. Map to the legacy `PreOrder` shape downstream recap components expect.
 *
 * Junction-derived fields (`messages`, `sms_messages`, `emails`) come back
 * as empty arrays — junctions provide the linked-chat IDs only, not the
 * rich message bodies (those are loaded on demand by the chat panel).
 */
export interface UseOfflineRecapPreOrdersParams {
  date: Date | string;
  dateField?: "delivery" | "order" | "all";
  start?: string;
  end?: string;
  openEnded?: boolean;
  endDate?: string;
  includeDeleted?: boolean;
  /**
   * Optional business-category gate. `0` = sales (the /recap default), `1` =
   * purchases (the /purchases route). When set, the Dexie cap is enforced
   * per-category so /recap and /purchases each get their own oldest-2000
   * window. When omitted, the legacy date-only compound is used (no
   * regression for mobile / non-mode-aware callers).
   */
  category?: 0 | 1;
  /**
   * Skip the heavy date-scope Dexie query and junction fetch. Used by the
   * workspace layout when a focused PO id is in the URL — the list query is
   * useless there but the hook must still be called to keep React's hook
   * order stable across list↔focus navigation. When false, both
   * `useLiveQuery` callbacks early-return empty; `data` resolves to `[]`.
   */
  enabled?: boolean;
}

export interface UseOfflineRecapPreOrdersResult {
  data: LegacyPreOrder[] | undefined;
  isLoading: boolean;
  error: Error | null;
  /**
   * True when the active date scope contained more than `RECAP_PO_HARD_CAP`
   * matching pre-orders and the recap is showing the oldest CAP. Consumer
   * (RecapShell) renders a banner telling the user to narrow the start
   * date forward to walk into more recent activity.
   */
  truncated: boolean;
  /**
   * True while `data` holds the current date scope's pre-orders but their
   * flow / linked-source rows are still being fetched (progressive-paint
   * window). Newly-scoped POs have `flows: []` during this window; the
   * recap renders a placeholder in the flow-table area and derived totals
   * should be treated as incomplete.
   */
  flowsPending: boolean;
  /**
   * True when the requested range is older than the kept HOT window (below the
   * floor) AND the on-demand fetch of that slice failed — offline or network
   * error. The recap shows "connect to load older orders" instead of an empty
   * list. False for in-window ranges and once the on-demand fetch succeeds
   * (rows then flow in via Dexie).
   */
  offlineMiss: boolean;
}

export function useOfflineRecapPreOrders({
  date,
  dateField = "delivery",
  start,
  end,
  openEnded,
  endDate,
  includeDeleted = false,
  category,
  enabled = true,
}: UseOfflineRecapPreOrdersParams): UseOfflineRecapPreOrdersResult {
  const queryClient = useQueryClient();
  const user = useUser();
  const companyId = user.company_id;
  // Scope every cache key by user id so an in-tab user switch (logout +
  // login as a different user, same company) doesn't replay the previous
  // user's visibility-scoped data — junction email metadata in particular
  // is filtered server-side per viewer, so two users with different roles
  // can resolve the SAME pre-order id set to DIFFERENT `linkedEmailMetaById`.
  const userId = user.id;
  const tz = useCompanyTimezone();
  const dateStr = typeof date === "string" ? date : format(date, "yyyy-MM-dd");

  // UTC range used for "order" / "all" date-field filtering — mirrors the
  // legacy server-side computeUTCRange so users on different timezones see
  // the same bucketing. Day boundaries are anchored on the company tz, not
  // the browser's, so a CA tenant viewed from Paris still buckets by CA day.
  const utcRange =
    dateField === "order" || dateField === "all"
      ? computeUTCRangeInTz(dateStr, tz, start, end, openEnded)
      : undefined;
  const startUTC = utcRange?.startUTC;
  const endUTC = utcRange?.endUTC;

  // --- Below-floor on-demand (STK-1323) ---------------------------------
  // When the WHOLE requested range is older than the kept HOT window, its rows
  // were never synced / were evicted. Fetch them on demand; they land in Dexie
  // (sync shape) and the live queries below pick them up reactively. The window
  // boundary is computed in UTC to MATCH the sync engine's windowStart (apps/web
  // sync-engine.windowStartFor), which is also UTC — this is an internal
  // sync-window boundary, not a company-tz display range. `openEnded`
  // ("starting_from") and the "all" axis span/overlap the window, so they stay
  // fully local (never below-floor).
  const windowStartStr = new Date(
    Date.now() - HOT_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  )
    .toISOString()
    .slice(0, 10);
  const onDemandParams = useMemo<OnDemandWindow & { enabled: boolean }>(() => {
    const disabled = {
      enabled: false as const,
      axis: "delivery" as const,
      from: "",
      to: "",
    };
    if (!enabled || openEnded || dateField === "all") return disabled;
    if (dateField === "delivery") {
      const to = endDate ?? dateStr;
      // Overlaps or inside the window → served locally from Dexie.
      if (to >= windowStartStr) return disabled;
      return { enabled: true, axis: "delivery", from: dateStr, to };
    }
    // order axis — absolute-UTC bounds (match the recap's own tz bucketing).
    if (!startUTC || !endUTC) return disabled;
    if (endUTC.slice(0, 10) >= windowStartStr) return disabled;
    return { enabled: true, axis: "order", fromTs: startUTC, toTs: endUTC };
  }, [
    enabled,
    openEnded,
    dateField,
    endDate,
    dateStr,
    startUTC,
    endUTC,
    windowStartStr,
  ]);
  const { loading: onDemandLoading, offlineMiss } =
    useOnDemandWindow(onDemandParams);

  // Deps signature passed through the slice so we can detect when the
  // useLiveQuery snapshot still reflects the PREVIOUS date (i.e. mid-
  // refresh after a date change). Powers the date-change loading state.
  // `enabled` is part of the cache key so the empty-shard cached under
  // focus mode (`enabled=false`) doesn't shadow the real list-mode shard
  // when the user navigates back. Without it, switching list→focus→list
  // could restore the empty result before the live query re-resolves.
  const depsKey = `${companyId ?? ""}|${userId ?? ""}|${dateField}|${dateStr}|${endDate ?? ""}|${startUTC ?? ""}|${endUTC ?? ""}|${openEnded ? 1 : 0}|${includeDeleted ? 1 : 0}|${enabled ? 1 : 0}|${category ?? "any"}`;

  // Split into TWO live queries (May 2026):
  //
  // The old single `useLiveQuery` read pre-orders AND junction tables in one
  // callback, so a write to the `preOrders` table (header field edit) re-fired
  // the whole thing — including the ~1 s `anyOf(ids).toArray()` x 4 junction
  // refetch, even though no junction row had actually changed. Date-change UX
  // ate two of those rebuilds (one for the local Dexie write, one for the SSE
  // echo): ~2 s of perceived spinner time for what should be ~50 ms.
  //
  // Dexie's `useLiveQuery` auto-tracks the tables each callback READS, so by
  // splitting the work along the table boundary we let each shard re-fire
  // independently:
  //   - Query 1 reads `preOrders` only      → re-fires on header edits, cheap.
  //   - Query 2 reads junction tables only  → re-fires on flow/message/etc
  //                                            edits, the pre-order set is
  //                                            consumed via closure on the
  //                                            current Query 1 snapshot.
  //
  // The two are merged in a `useMemo` below so downstream `RecapSlice`
  // consumers see no shape change. The brief window after a date-range change
  // (Query 1 has new POs, Query 2 still has old junctions) is guarded by the
  // `forIdsKey` mismatch — combine swallows the mismatch and keeps the
  // previous combined slice on screen until junctions catch up.
  const preOrderSlice = useLiveQuery(
    async () => {
      // Disabled (focus mode): skip the heavy date-scope query entirely. No
      // table reads, no Dexie subscription tracking. Returns the same empty
      // shape so downstream consumers see the same surface as a cold list.
      if (!enabled) {
        return { ...EMPTY_PRE_ORDER_SLICE, depsKey };
      }
      const tTotal = perfMark();
      if (!companyId) {
        const empty: PreOrderSlice = { ...EMPTY_PRE_ORDER_SLICE, depsKey };
        rememberShard(preOrderShardCache, depsKey, empty);
        return empty;
      }

      const tPos = perfMark();
      const { rows: rawPreOrders, truncated } = await fetchScopedPreOrders({
        companyId,
        dateField,
        dateStr,
        endDate,
        startUTC,
        endUTC,
        openEnded,
        category,
      });
      const preOrders = includeDeleted
        ? rawPreOrders
        : rawPreOrders.filter((r) => !r.deletedAt);
      perfMeasure(
        `offlineShim.fetchScopedPreOrders (${preOrders.length} POs${truncated ? ` of >${RECAP_PO_HARD_CAP}, truncated` : ""}, indexed)`,
        tPos,
      );
      if (truncated) {
        // One-line warn so a user / dev who hits the cap sees it in the
        // console without enabling RECAP_PERF. Not throttled — useLiveQuery
        // memoizes by deps, so this fires once per (date, mode) slice, not
        // per render.
        console.warn(
          `[recap] pre-order cap engaged: showing oldest ${RECAP_PO_HARD_CAP} of >${RECAP_PO_HARD_CAP} matching orders. ` +
            `Narrow the start date forward to walk into more recent activity.`,
        );
      }
      perfMeasure(
        `offlineShim.preOrderShard (${preOrders.length} POs)`,
        tTotal,
      );
      const fresh: PreOrderSlice = { preOrders, depsKey, truncated };
      // Stash the fresh result so the next remount (e.g. back-nav from
      // /recap/:id) gets it as initial value instead of EMPTY.
      rememberShard(preOrderShardCache, depsKey, fresh);
      return fresh;
    },
    [
      enabled,
      companyId,
      dateField,
      dateStr,
      endDate,
      startUTC,
      endUTC,
      openEnded,
      includeDeleted,
      category,
    ],
    // Restore the last-known slice for this exact deps key. On remount the
    // user sees the previous list synchronously; Dexie still re-runs the
    // callback above to validate. `isStale` downstream gates spinner copy.
    preOrderShardCache.get(depsKey) ?? EMPTY_PRE_ORDER_SLICE,
  );

  // Stable signature of the in-scope PO ids — only changes when the SET of
  // visible pre-orders changes (date range moved, a PO entered/left scope).
  // Drives the junction query's deps: the heavy `fetchJunctions` only
  // re-resolves when the id set actually changes, not on every pre-order
  // field edit.
  //
  // Sort + join keeps the key stable under reordering — a date sort within
  // the same id set should NOT bust the junction cache.
  const preOrderIdsKey = useMemo(() => {
    if (preOrderSlice.preOrders.length === 0) return "";
    const ids = preOrderSlice.preOrders.map((p) => p.id);
    ids.sort();
    return ids.join(",");
  }, [preOrderSlice.preOrders]);

  // Secondary signature: PO id + the legacy direct `emailId` field per row.
  // The junction query reads `preOrderEmails` (junction rows) AND consults
  // `po.emailId` indirectly through `resolveLinkedEmailMeta` when stitching
  // linked-email metadata onto pre-orders. A pre-order whose `emailId` flips
  // (server links / unlinks an email after creation) wouldn't otherwise
  // re-fire the junction query — `preOrderIdsKey` alone wouldn't change, and
  // Dexie's reactive tracking only watches the `emails` / `preOrderEmails`
  // tables, not writes to the `preOrders` row's `emailId` column. Including
  // it in the deps key forces a re-resolve so the metadata catches up.
  const preOrderEmailIdsKey = useMemo(() => {
    if (preOrderSlice.preOrders.length === 0) return "";
    const pairs = preOrderSlice.preOrders.map(
      (p) => `${p.id}:${p.emailId ?? ""}`,
    );
    pairs.sort();
    return pairs.join(",");
  }, [preOrderSlice.preOrders]);

  // Composite key for the junction shard cache. Pulls in every input that
  // can change the resolved junction set so a remount with identical
  // signals reuses the previous result instead of refetching. Includes
  // `userId` because junction email metadata is visibility-scoped per
  // viewer server-side (see comment on `depsKey` above).
  const junctionCacheKey = `${userId ?? ""}|${preOrderIdsKey}|${preOrderEmailIdsKey}|${includeDeleted ? 1 : 0}`;

  const junctionSlice = useLiveQuery(
    async () => {
      if (preOrderIdsKey === "") return EMPTY_JUNCTION_SLICE;
      // Closure capture is fine — Dexie's `useLiveQuery` re-creates this
      // callback on every render but only re-resolves when deps (or any
      // read table) changes, and on resolution it sees the latest
      // `preOrderSlice.preOrders`.
      const preOrders = preOrderSlice.preOrders;
      const ids = preOrders.map((p) => p.id);
      const tJunc = perfMark();
      const live = <T extends { deletedAt?: string | null }>(rows: T[]) =>
        includeDeleted ? rows : rows.filter((r) => !r.deletedAt);
      // Per-id `.equals()` in a read transaction, NOT `.anyOf(ids)` — same
      // result, ~5× faster under client-hardware CPU throttle, still tracked
      // by useLiveQuery. See `fetchJunctionByPreOrderIds`.
      //
      // The email branch chains its per-email metadata join inside the same
      // fan-out: the meta lookup only depends on the email junction rows (+
      // the PO snapshot already in hand), so running it here overlaps it
      // with the flow/message/sms walks instead of serializing it after all
      // four — previously ~1 s of junction fetch followed by the meta fetch
      // back-to-back.
      const [flows, messages, smsMessages, emailJoin] = await Promise.all([
        fetchJunctionByPreOrderIds<OfflinePreOrderFlow>(
          PRE_ORDER_FLOW_TABLE,
          ids,
        ),
        fetchJunctionByPreOrderIds<OfflinePreOrderMessage>(
          PRE_ORDER_MESSAGE_TABLE,
          ids,
        ),
        fetchJunctionByPreOrderIds<OfflinePreOrderSmsMessage>(
          PRE_ORDER_SMS_MESSAGE_TABLE,
          ids,
        ),
        (async () => {
          const emails = await fetchJunctionByPreOrderIds<OfflinePreOrderEmail>(
            PRE_ORDER_EMAIL_TABLE,
            ids,
          );
          const liveEmailJunctions = live(emails);
          const linkedEmailMetaById = await resolveLinkedEmailMeta(
            liveEmailJunctions,
            preOrders,
          );
          return { liveEmailJunctions, linkedEmailMetaById };
        })(),
      ]);
      const { liveEmailJunctions, linkedEmailMetaById } = emailJoin;
      perfMeasure(
        `offlineShim.fetchJunctions (flows=${flows.length}, msg=${messages.length}, sms=${smsMessages.length}, email=${liveEmailJunctions.length}, emailMeta=${linkedEmailMetaById.size})`,
        tJunc,
      );
      const fresh: JunctionSlice = {
        flows: live(flows),
        messages: live(messages),
        smsMessages: live(smsMessages),
        emails: liveEmailJunctions,
        linkedEmailMetaById,
        forIdsKey: preOrderIdsKey,
      };
      // Same remount-restoration pattern as `preOrderShardCache` — stash
      // the resolved junctions so the next mount of this hook with the
      // same id set sees them as the initial value.
      rememberShard(junctionShardCache, junctionCacheKey, fresh);
      return fresh;
    },
    [userId, preOrderIdsKey, preOrderEmailIdsKey, includeDeleted],
    junctionShardCache.get(junctionCacheKey) ?? EMPTY_JUNCTION_SLICE,
  );

  // Combined slice. Progressive paint (June 2026): when the junction shard
  // still reflects a previous id set (`forIdsKey` mismatch — the window
  // right after a date-scope change), publish the CURRENT pre-orders
  // immediately with the stale junction rows riding along and
  // `junctionsPending: true`, instead of holding the whole view back ~1 s
  // until the junction fetch lands. Why the stale rows are safe to keep:
  //   - all junction-derived fields are looked up per-PO id downstream
  //     (`flowsByPo.get(po.id)` etc.), so rows for departed POs are simply
  //     never read;
  //   - POs that persist across the change (e.g. an SSE-added PO joining
  //     the current set) keep their existing flows instead of flashing
  //     flow-less for a frame.
  // Only genuinely NEW POs render flow-less during the pending window —
  // the recap shows their headers with a placeholder where the flow rows
  // will land. When junctions catch up, the consistent slice publishes and
  // the flag drops.
  const slice = useMemo<RecapSlice>(() => {
    const junctionsMatch =
      junctionSlice.forIdsKey === preOrderIdsKey || preOrderIdsKey === "";
    return {
      preOrders: preOrderSlice.preOrders,
      depsKey: preOrderSlice.depsKey,
      truncated: preOrderSlice.truncated,
      flows: junctionSlice.flows,
      messages: junctionSlice.messages,
      smsMessages: junctionSlice.smsMessages,
      emails: junctionSlice.emails,
      linkedEmailMetaById: junctionSlice.linkedEmailMetaById,
      junctionsPending: !junctionsMatch,
    };
  }, [preOrderSlice, junctionSlice, preOrderIdsKey]);

  // Defer the slice so the heavy downstream cascade (mapping, enrich,
  // proposals merge, partner/product grouping, virtualized list mount) runs
  // in a low-priority concurrent render. Without this, when Dexie resolves
  // React commits all that work in one synchronous pass — the spinner
  // appears to "freeze" for ~500 ms right before the list paints because
  // the last committed frame (spinner) can't repaint while React is
  // blocking on the high-priority render. With `useDeferredValue`, the
  // urgent render keeps the spinner on screen (deferredSlice still points
  // at the previous depsKey ⇒ `isStale = true`); React then schedules the
  // heavy render as an interruptible transition, yielding to the browser
  // so the CSS-animated spinner keeps spinning until the new list commits.
  const deferredSlice = useDeferredValue(slice);
  const isStale = deferredSlice.depsKey !== depsKey;

  // Per-PO cache so a single-row edit doesn't rebuild 1,250 LegacyPreOrder
  // objects from scratch. The liveQuery callback re-runs on ANY write to any
  // tracked table (preOrders, flows, junctions); without this cache every fire
  // produced fresh `LegacyPreOrder` refs for every PO in scope, defeating
  // downstream structural sharing (`useRecapFiltering`) and forcing the
  // `seedDetailCaches` loop below to setQueryData() for all 1,250 POs every
  // cycle (~1.5s per cycle on prod-size tenants). Cache key:
  //   updatedAt + sorted flow (id:updatedAt) sig + sorted junction id sig.
  // Hit ⇒ reuse last cycle's mapped object (same ref); miss ⇒ rebuild and
  // store. Eviction: the cache is rebuilt on every render from currently-
  // visible POs, so stale entries drop off naturally.
  const poCacheRef = useRef(
    new Map<
      string,
      {
        sig: string;
        mapped: LegacyPreOrder;
      }
    >(),
  );

  // Map offline shape → legacy shape. Pure derivation; enrichment writes
  // joined partner/product/unit/location into the array. Memo separately so
  // re-renders that don't change the slice don't redo the mapping. Reads
  // from `deferredSlice` so the build runs in the low-priority concurrent
  // render — see the `useDeferredValue` block above.
  const data = useMemo<LegacyPreOrder[]>(() => {
    const tBuild = perfMark();

    const flowsByPo = new Map<string, OfflinePreOrderFlow[]>();
    for (const f of deferredSlice.flows) {
      if (!f.preOrderId) continue;
      const arr = flowsByPo.get(f.preOrderId) ?? [];
      arr.push(f);
      flowsByPo.set(f.preOrderId, arr);
    }

    const whatsappChatIdsByPo = new Map<string, Set<string>>();
    for (const j of deferredSlice.messages) {
      if (!j.preOrderId || !j.chatId) continue;
      const set = whatsappChatIdsByPo.get(j.preOrderId) ?? new Set<string>();
      set.add(j.chatId);
      whatsappChatIdsByPo.set(j.preOrderId, set);
    }
    const smsChatIdsByPo = new Map<string, Set<string>>();
    for (const j of deferredSlice.smsMessages) {
      if (!j.preOrderId || !j.smsChatId) continue;
      const set = smsChatIdsByPo.get(j.preOrderId) ?? new Set<string>();
      set.add(j.smsChatId);
      smsChatIdsByPo.set(j.preOrderId, set);
    }
    const emailIdsByPo = new Map<string, Set<string>>();
    for (const j of deferredSlice.emails) {
      if (!j.preOrderId || !j.emailId) continue;
      const set = emailIdsByPo.get(j.preOrderId) ?? new Set<string>();
      set.add(j.emailId);
      emailIdsByPo.set(j.preOrderId, set);
    }

    // Per-PO linked-message ids (WhatsApp + SMS) derived from the junction
    // rows. Drives the highlight border on linked messages via
    // `pre_order_metadata.message_ids`. Each channel is sorted oldest→newest
    // by `createdAt` then concatenated (WA then SMS) — cross-channel
    // interleave isn't needed for the highlight to anchor correctly. The
    // junction `messageId` IS the message row id, so it matches the rendered
    // bubble directly.
    const messageIdsByPo = buildMessageIdsByPo(
      deferredSlice.messages,
      deferredSlice.smsMessages,
    );

    const nextCache = new Map<
      string,
      { sig: string; mapped: LegacyPreOrder }
    >();
    let hits = 0;
    const mapped: LegacyPreOrder[] = deferredSlice.preOrders.map((po) => {
      const poFlows = flowsByPo.get(po.id) ?? [];
      const wa = whatsappChatIdsByPo.get(po.id);
      const sm = smsChatIdsByPo.get(po.id);
      const em = emailIdsByPo.get(po.id);
      const msgIds = messageIdsByPo.get(po.id) ?? [];
      const sig = buildPoSignature(
        po,
        poFlows,
        wa,
        sm,
        em,
        msgIds,
        deferredSlice.linkedEmailMetaById,
      );
      const prev = poCacheRef.current.get(po.id);
      if (prev?.sig === sig) {
        // Known limitation: `prev.mapped` carries the enriched
        // partner / product / unit / location fields that
        // `enrichRecapPreOrders` mutated onto it the first time we
        // produced this PO. The signature doesn't include the global
        // products / partners cache versions, so if a global edits
        // mid-session (rename a partner, update a product) the cached
        // PO's enriched fields stay frozen until either the signature
        // changes (a flow / PO write moves `updatedAt`) or the user
        // reloads the recap. Tracked as a follow-up — fixing it
        // without losing the structural-sharing win this cache buys
        // means either tagging the signature with global cache
        // versions or splitting the cache into a "lean" form that's
        // re-enriched on every output (loses ref stability).
        nextCache.set(po.id, prev);
        hits++;
        return prev.mapped;
      }
      const fresh = mapOfflinePreOrder(po, poFlows, {
        whatsappChatIds: wa,
        smsChatIds: sm,
        emailIds: em,
        messageIds: msgIds,
        linkedEmailMeta: deferredSlice.linkedEmailMetaById,
      });
      nextCache.set(po.id, { sig, mapped: fresh });
      return fresh;
    });
    poCacheRef.current = nextCache;

    mapped.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    enrichRecapPreOrders(queryClient, mapped);
    perfMeasure(
      `offlineShim.buildLegacy (${mapped.length} POs, ${hits} cache hits)`,
      tBuild,
    );
    return mapped;
  }, [deferredSlice, queryClient]);

  // No TanStack seed: the three consumers of the per-id detail / flows
  // caches (chat panel deep-link, /recap focus view, AI proposal cards)
  // were migrated to `useOfflineFocusPreOrder` (Dexie-direct, junction-
  // enriched). Recap data lives in Dexie; double-writing it into the
  // TanStack cache was pure tech debt that cost ~3.2 s of chunked
  // background work on each new batch.

  return {
    data,
    // A below-floor on-demand fetch in flight counts as loading, so the recap
    // shows the "Loading orders…" pill instead of a misleading empty "no
    // orders" state while the older-orders slice is being fetched (STK-1323).
    isLoading: isStale || onDemandLoading,
    error: null,
    truncated: deferredSlice.truncated,
    // Read from the deferred slice so the flag matches the data actually
    // rendered (`data` is built from `deferredSlice` too).
    flowsPending: deferredSlice.junctionsPending,
    offlineMiss,
  };
}

interface ScopeArgs {
  companyId: string;
  dateField: "delivery" | "order" | "all";
  dateStr: string;
  endDate?: string;
  startUTC?: string;
  endUTC?: string;
  openEnded?: boolean;
  /**
   * Optional business-category gate. When set, the `.limit(CAP+1)` cap is
   * enforced per-category via the `[companyId+category+deliveryDate]` /
   * `[companyId+category+orderDate]` compound indexes — so /recap and
   * /purchases each get their own oldest-N-of-category window instead of
   * sharing one combined cap. When undefined, the legacy date-only
   * compounds are used and behavior is unchanged.
   */
  category?: 0 | 1;
}

/**
 * Pure date-scope matcher used by the "all" mode in-memory pass and exposed
 * for tests. Returns `true` when the PO falls inside the active scope:
 * - "delivery": deliveryDate is inside [dateStr, endDate ?? dateStr] (open
 *   upper bound when `openEnded`).
 * - "order": orderDate (or createdAt fallback) is inside the UTC range
 *   (open upper bound when `openEnded` or `endUTC` is missing).
 * - "all": EITHER delivery match OR order match.
 *
 * Mirrors the client-side virtual-PO filter (`isVirtualPreOrderInDateRange`)
 * so real and virtual rows surface together under the same scope.
 */
export function isPreOrderInScope(
  po: Pick<OfflinePreOrder, "deliveryDate" | "orderDate" | "createdAt">,
  scope: Omit<ScopeArgs, "companyId">,
): boolean {
  const deliveryUpper = computeDeliveryUpperBound(
    scope.dateStr,
    scope.endDate,
    scope.openEnded,
  );

  if (scope.dateField === "delivery") {
    return matchesDeliveryRange(po, scope.dateStr, deliveryUpper);
  }
  if (scope.dateField === "order") {
    if (!scope.startUTC) return false;
    return matchesUTCRange(
      po,
      { startUTC: scope.startUTC, endUTC: scope.endUTC },
      scope.openEnded,
    );
  }
  // "all"
  const deliveryMatch =
    !!scope.dateStr && matchesDeliveryRange(po, scope.dateStr, deliveryUpper);
  if (deliveryMatch) return true;
  if (!scope.startUTC) return false;
  return matchesUTCRange(
    po,
    { startUTC: scope.startUTC, endUTC: scope.endUTC },
    scope.openEnded,
  );
}

export function computeDeliveryUpperBound(
  dateStr: string,
  endDate: string | undefined,
  openEnded: boolean | undefined,
): string {
  if (openEnded) return DATE_UPPER_BOUND;
  return endDate ?? dateStr;
}

/**
 * Indexed Dexie query for the date-scoped pre-orders. Always company-scoped
 * via the compound `[companyId+deliveryDate]` / `[companyId+orderDate]`
 * indexes added in DB v30 — protects against cross-company leakage when
 * IndexedDB holds rows from more than one company (e.g. after a session
 * switches companies and the pre-order table hasn't been cleared yet).
 *
 * Touches only the rows the active date scope cares about — the rest of
 * the company table never enters JS memory.
 */
/**
 * Resolve per-email metadata (user_id + message_id) for every email
 * referenced by any visible PO — junction rows AND each PO's forward FK
 * (covers the race window where a PO insert lands before its fan-out, or
 * legacy pre-junction data).
 *
 * Presence in the returned map is the visibility signal: an id that
 * doesn't resolve to a Dexie row is one the viewer can't read per
 * `emailVisibilityFilter`. `messageId` is optional — a row pre-backfill
 * still counts as visible (the user can open it) but can't participate
 * in sibling-collapse until the backfill stamps it.
 *
 * Uses `bulkGet` — `id` is the table's primary key, so this is a direct
 * key lookup. The previous `.where("id").anyOf(ids)` walked the same keys
 * through Dexie's CPU-bound anyOf machinery and cost ~2 s for ~400 ids on
 * real client hardware; `bulkGet` resolves the same rows in tens of ms and
 * is still tracked by `liveQuery`. Exported for the parity test.
 */
export async function resolveLinkedEmailMeta(
  liveEmailJunctions: OfflinePreOrderEmail[],
  preOrders: Pick<OfflinePreOrder, "emailId">[],
): Promise<Map<string, { user_id: string; message_id?: string }>> {
  const allLinkedEmailIds = new Set<string>();
  for (const j of liveEmailJunctions) {
    if (j.emailId) allLinkedEmailIds.add(j.emailId);
  }
  for (const po of preOrders) {
    if (po.emailId) allLinkedEmailIds.add(po.emailId);
  }
  const out = new Map<string, { user_id: string; message_id?: string }>();
  if (allLinkedEmailIds.size === 0) return out;

  const tEmailMeta = perfMark();
  const emailRows = await offlineDb
    .table<Email>(EMAIL_TABLE)
    .bulkGet(Array.from(allLinkedEmailIds));
  for (const r of emailRows) {
    if (r?.id && r?.userId) {
      out.set(r.id, {
        user_id: r.userId,
        message_id: r.messageId ?? undefined,
      });
    }
  }
  perfMeasure(
    `offlineShim.fetchEmailMeta (linkedIds=${allLinkedEmailIds.size}, resolved=${out.size})`,
    tEmailMeta,
  );
  return out;
}

async function fetchScopedPreOrders(
  args: ScopeArgs,
): Promise<{ rows: OfflinePreOrder[]; truncated: boolean }> {
  const {
    companyId,
    dateField,
    dateStr,
    endDate,
    startUTC,
    endUTC,
    openEnded,
    category,
  } = args;
  const table = offlineDb.table<OfflinePreOrder>(PRE_ORDER_TABLE);

  // Probe one row past the cap so we can flag truncation without paying for
  // a full .count(). Compound indexes return rows in ascending key order, so
  // "first N" = oldest in the date window — matches the "starts at this date"
  // mental model of starting_from mode: narrow the start date forward to
  // walk into more recent activity.
  const probeLimit = RECAP_PO_HARD_CAP + 1;
  const trim = (rows: OfflinePreOrder[]) => ({
    rows: rows.slice(0, RECAP_PO_HARD_CAP),
    truncated: rows.length > RECAP_PO_HARD_CAP,
  });

  if (dateField === "delivery") {
    // Date-only string compare on `deliveryDate` (values are YYYY-MM-DD
    // calendar dates, no time component). `openEnded` (rangeMode
    // "starting_from") leaves `endDate` undefined — without the upper
    // bound override the query collapses to a single day instead of
    // "from dateStr onwards".
    const upper = computeDeliveryUpperBound(dateStr, endDate, openEnded);
    const tBuild = perfMark();
    // When `category` is provided, hit the `[companyId+category+deliveryDate]`
    // compound so the `.limit(CAP+1)` cap is enforced per-category — the
    // fix for the silent purchase-row drop behind the merged sales+purchases
    // oldest-2000 set. When undefined, fall back to the date-only compound.
    const query =
      category == null
        ? table
            .where("[companyId+deliveryDate]")
            .between([companyId, dateStr], [companyId, upper], true, true)
            .limit(probeLimit)
        : table
            .where("[companyId+category+deliveryDate]")
            .between(
              [companyId, category, dateStr],
              [companyId, category, upper],
              true,
              true,
            )
            .limit(probeLimit);
    perfMeasure("offlineShim.fetchScopedPreOrders.buildQuery", tBuild, 1);
    const tAwait = perfMark();
    const rows = await query.toArray();
    perfMeasure(
      `offlineShim.fetchScopedPreOrders.toArray.delivery (${rows.length} rows, category=${category ?? "any"})`,
      tAwait,
      1,
    );
    return trim(rows);
  }

  if (dateField === "order") {
    if (!startUTC) return { rows: [], truncated: false };
    const upper = openEnded || !endUTC ? DATE_UPPER_BOUND : endUTC;
    const tAwait = perfMark();
    const rows =
      category == null
        ? await table
            .where("[companyId+orderDate]")
            .between([companyId, startUTC], [companyId, upper], true, true)
            .limit(probeLimit)
            .toArray()
        : await table
            .where("[companyId+category+orderDate]")
            .between(
              [companyId, category, startUTC],
              [companyId, category, upper],
              true,
              true,
            )
            .limit(probeLimit)
            .toArray();
    perfMeasure(
      `offlineShim.fetchScopedPreOrders.toArray.order (${rows.length} rows, openEnded=${openEnded ? "yes" : "no"}, category=${category ?? "any"})`,
      tAwait,
      1,
    );
    return trim(rows);
  }

  // dateField === "all". Walk the company-scoped slice via the plain
  // `companyId` index (catches rows with null date fields that the
  // compound indexes exclude) and apply the OR-of-(delivery,order)
  // in JS via `isPreOrderInScope` — the "all" view is rarely hit, and
  // the companyId scope keeps cross-tenant rows out.
  //
  // The Dexie `.limit()` can't be pushed before the JS filter: the
  // `companyId` index has no date ordering, so capping at CAP+1 here
  // would return an arbitrary subset and silently drop in-scope rows
  // sitting beyond position CAP+1. The "all" view is rare; we eat the
  // full company-scan cost so the post-filter `truncated` flag and the
  // banner copy ("oldest CAP matching the date range") are accurate.
  const all = await table.where("companyId").equals(companyId).toArray();
  // When `category` is set, gate by it before the date-scope filter so the
  // cap is enforced on the per-category subset.
  const categoryGated =
    category == null ? all : all.filter((po) => po.category === category);
  if (!startUTC && !dateStr) {
    // No scope at all — cap the (category-gated) set and report truncation
    // directly.
    return {
      rows: categoryGated.slice(0, RECAP_PO_HARD_CAP),
      truncated: categoryGated.length > RECAP_PO_HARD_CAP,
    };
  }
  const scope = { dateField, dateStr, endDate, startUTC, endUTC, openEnded };
  const filtered = categoryGated.filter((po) => isPreOrderInScope(po, scope));
  return {
    rows: filtered.slice(0, RECAP_PO_HARD_CAP),
    // Truncation is derived from the in-scope set only, so a company with
    // many out-of-scope POs doesn't trip a false-positive banner on a
    // narrow date range.
    truncated: filtered.length > RECAP_PO_HARD_CAP,
  };
}

function matchesDeliveryRange(
  po: Pick<OfflinePreOrder, "deliveryDate">,
  lower: string,
  upper: string,
): boolean {
  if (!po.deliveryDate) return false;
  return po.deliveryDate >= lower && po.deliveryDate <= upper;
}

function matchesUTCRange(
  po: Pick<OfflinePreOrder, "orderDate" | "createdAt">,
  range: { startUTC: string; endUTC?: string },
  openEnded?: boolean,
): boolean {
  const t = po.orderDate ?? po.createdAt;
  if (!t) return false;
  const ms = new Date(t.endsWith("Z") ? t : t + "Z").getTime();
  const startMs = new Date(range.startUTC + "Z").getTime();
  if (ms < startMs) return false;
  if (range.endUTC && !openEnded) {
    const endMs = new Date(range.endUTC + "Z").getTime();
    if (ms > endMs) return false;
  }
  return true;
}

interface JunctionIds {
  whatsappChatIds?: Set<string>;
  smsChatIds?: Set<string>;
  emailIds?: Set<string>;
  /**
   * Linked WhatsApp + SMS message row ids, chronologically ordered. Folded
   * into `pre_order_metadata.message_ids` so linked messages get the highlight
   * border even for newer pre-orders that only carry junction rows.
   */
  messageIds?: string[];
  /**
   * Per-email-id { user_id, message_id? } map for every email referenced by
   * this PO's junctions that is present in the viewer's Dexie. Used to emit
   * `linked_emails[]` with enough metadata for `useLinkedSources` to group
   * sibling rows by `message_id` and pick the entry owned by the current
   * user. **Presence is the visibility signal**: a junction id missing from
   * this map points at an email row the viewer can't read (cross-user dedup
   * sibling per `emailVisibilityFilter`), so the popover skips it entirely
   * rather than emitting a non-clickable stub.
   */
  linkedEmailMeta?: Map<string, { user_id: string; message_id?: string }>;
}

interface JunctionMessageRow {
  preOrderId: string | null;
  messageId: string | null;
  createdAt?: string;
}

/**
 * Build the per-PO linked-message-id map from the WhatsApp + SMS junction
 * rows. Each channel is sorted oldest→newest by `createdAt` (`localeCompare`
 * on the ISO strings keeps it deterministic and satisfies Sonar's
 * no-raw-string-sort rule) then concatenated WA-then-SMS. Rows with a
 * null/empty `messageId` or `preOrderId` are skipped; ids are deduped per PO.
 */
function buildMessageIdsByPo(
  waRows: JunctionMessageRow[],
  smsRows: JunctionMessageRow[],
): Map<string, string[]> {
  const byChannel = (rows: JunctionMessageRow[]) => {
    const grouped = new Map<string, JunctionMessageRow[]>();
    for (const r of rows) {
      if (!r.preOrderId || !r.messageId) continue;
      const arr = grouped.get(r.preOrderId) ?? [];
      arr.push(r);
      grouped.set(r.preOrderId, arr);
    }
    return grouped;
  };
  const wa = byChannel(waRows);
  const sms = byChannel(smsRows);

  const sortedIds = (rows: JunctionMessageRow[] | undefined): string[] => {
    if (!rows) return [];
    return [...rows]
      .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""))
      .map((r) => r.messageId as string);
  };

  const result = new Map<string, string[]>();
  const poIds = new Set<string>([...wa.keys(), ...sms.keys()]);
  for (const poId of poIds) {
    const ordered = [...sortedIds(wa.get(poId)), ...sortedIds(sms.get(poId))];
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const id of ordered) {
      if (seen.has(id)) continue;
      seen.add(id);
      deduped.push(id);
    }
    result.set(poId, deduped);
  }
  return result;
}

/**
 * Cheap content fingerprint for the per-PO cache. Stable across cycles when
 * the PO + its flows + its junction id sets haven't moved. Flow `updatedAt`
 * is bumped by every Drizzle write, so any cell edit invalidates the entry.
 * Implements the May 2026 "Structural sharing MISS" fix tracked on dev —
 * `useOfflineRecapPreOrders` keeps a per-PO cache keyed on this signature
 * so unchanged POs return the prior `LegacyPreOrder` reference and stop
 * cascading re-renders through `RecapContext` / `PartnerListView`.
 */
export function buildPoSignature(
  po: OfflinePreOrder,
  flows: OfflinePreOrderFlow[],
  whatsappChatIds: Set<string> | undefined,
  smsChatIds: Set<string> | undefined,
  emailIds: Set<string> | undefined,
  messageIds: string[],
  linkedEmailMeta: Map<string, { user_id: string; message_id?: string }>,
): string {
  const flowSig: string[] = new Array(flows.length);
  for (let i = 0; i < flows.length; i++) {
    const f = flows[i]!;
    flowSig[i] = `${f.id}:${f.updatedAt ?? ""}:${f.deletedAt ?? ""}`;
  }
  // Locale-aware compare keeps the signature deterministic across runtimes
  // even though the inputs are ASCII UUIDs / IDs — silences the Sonar
  // bad-practice rule that flags raw `.sort()` on strings.
  const byLocale = (a: string, b: string) => a.localeCompare(b);
  flowSig.sort(byLocale);
  const wa = whatsappChatIds
    ? [...whatsappChatIds].sort(byLocale).join(",")
    : "";
  const sm = smsChatIds ? [...smsChatIds].sort(byLocale).join(",") : "";
  const em = emailIds ? [...emailIds].sort(byLocale).join(",") : "";
  // Per-PO email-meta fingerprint: covers both junction-derived email ids AND
  // the PO's own forward FK (po.emailId) so a `message_id` backfill on the FK
  // email invalidates the cache even when no junction row exists yet.
  // Folds in user_id + message_id so a `message_id` backfill on a previously-
  // bare entry invalidates the cache and `linked_emails[]` re-emits with
  // resolved meta on the next pass.
  let emMetaSig = "";
  {
    const allEmailIds = new Set(emailIds ?? []);
    if (po.emailId) allEmailIds.add(po.emailId);
    if (allEmailIds.size > 0) {
      const parts: string[] = [];
      for (const id of [...allEmailIds].sort(byLocale)) {
        const meta = linkedEmailMeta.get(id);
        parts.push(`${id}:${meta?.user_id ?? ""}:${meta?.message_id ?? ""}`);
      }
      emMetaSig = parts.join(",");
    }
  }
  // `messageIds` is already deterministically ordered by buildMessageIdsByPo,
  // so it folds in without a re-sort.
  return `${po.updatedAt ?? ""}|${po.deletedAt ?? ""}|${flowSig.join(
    "|",
  )}|w:${wa}|s:${sm}|e:${em}|em:${emMetaSig}|m:${messageIds.join(",")}`;
}

export function mapOfflinePreOrder(
  po: OfflinePreOrder,
  flows: OfflinePreOrderFlow[],
  junctions: JunctionIds = {},
): LegacyPreOrder {
  const sortedFlows = [...flows].sort((a, b) => {
    const ai = a.flowIndex ?? Number.MAX_SAFE_INTEGER;
    const bi = b.flowIndex ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  // Hydrate the linked-chat arrays from the junctions. Always include the
  // PO's own forward FK (whatsapp_chat_id / sms_chat_id) so the trigger
  // user's chat still matches even if the junction row hasn't landed yet
  // via SSE. The email FK gets the same union but is then filtered by
  // Dexie-visibility below — a cross-user dedup sibling that the viewer
  // can't read shouldn't surface as a non-clickable popover stub.
  const linkedWhatsapp = new Set(junctions.whatsappChatIds ?? []);
  if (po.whatsappChatId) linkedWhatsapp.add(po.whatsappChatId);
  const linkedSms = new Set(junctions.smsChatIds ?? []);
  if (po.smsChatId) linkedSms.add(po.smsChatId);
  const candidateEmailIds = new Set(junctions.emailIds ?? []);
  if (po.emailId) candidateEmailIds.add(po.emailId);

  // Visibility gate: only emit linked-email entries the viewer can actually
  // open. The shim's `linkedEmailMeta` map contains exactly the ids whose
  // email row landed in this user's Dexie (i.e. passed the API-side
  // `emailVisibilityFilter`). Ids absent from the map point at sibling
  // copies owned by other users in the same dedup group; the junction row
  // exists on the PO (fanned out to every participant) but the email body
  // is invisible to this viewer, so we drop them rather than render a
  // "Email message" stub the user can never preview.
  //
  // SSE race trade-off: a junction that lands before its email row will
  // briefly miss the gate and re-appear on the next live-query fire when
  // Dexie resolves the row. Transient absence beats a permanent stub.
  const linkedEmailMeta = junctions.linkedEmailMeta;
  const linkedEmails: PreOrderLinkedEmail[] = [];
  const linkedEmail = new Set<string>();
  for (const id of candidateEmailIds) {
    const meta = linkedEmailMeta?.get(id);
    if (!meta) continue;
    linkedEmail.add(id);
    linkedEmails.push({
      id,
      user_id: meta.user_id,
      message_id: meta.message_id,
    });
  }

  return {
    id: po.id,
    user_id: po.userId ?? "",
    group_id: po.groupId ?? null,
    type: po.type,
    category: po.category,
    status: po.status,
    source_type: po.sourceType,
    partner_id: po.partnerId ?? undefined,
    shipping_address_id: po.shippingAddressId,
    location_id: po.locationId,
    route_id: po.routeId,
    buyer_id: po.buyerId ?? undefined,
    employee_id: po.employeeId,
    whatsapp_chat_id: po.whatsappChatId ?? undefined,
    sms_chat_id: po.smsChatId ?? undefined,
    email_id: po.emailId ?? undefined,
    order_id: po.orderId,
    pre_order_number: po.preOrderNumber,
    customer_po_number: po.customerPoNumber,
    comment: po.comment,
    delivery_date: po.deliveryDate,
    order_date: po.orderDate,
    created_at: po.createdAt,
    updated_at: po.updatedAt,
    deleted_at: po.deletedAt,
    pre_order_metadata: mergeMessageIdsIntoMetadata(
      (po.preOrderMetadata as PreOrderMetadata | null) ?? null,
      junctions.messageIds ?? [],
    ),
    shared_with_user_ids: po.sharedWithUserIds ?? [],
    flows: sortedFlows.map(mapOfflineFlow),
    // Inline message bodies are loaded on demand by the chat panel, not
    // here. Junctions only provide the linked-chat ID Sets above.
    messages: [],
    sms_messages: [],
    emails: [],
    linked_emails: linkedEmails,
    linked_whatsapp_chat_ids: Array.from(linkedWhatsapp),
    linked_sms_chat_ids: Array.from(linkedSms),
    linked_email_ids: Array.from(linkedEmail),
  };
}

function mapOfflineFlow(f: OfflinePreOrderFlow): LegacyPreOrderFlow {
  return {
    id: f.id,
    pre_order_id: f.preOrderId ?? "",
    product_id: f.productId,
    partner_id: f.partnerId,
    quantity: f.quantity,
    weight: f.weight,
    unit_id: f.unitId,
    price_per_weight: f.pricePerWeight,
    comment: f.comment,
    hidden_comment: f.hiddenComment,
    status: f.status,
    index: f.flowIndex,
    delivery_date: f.deliveryDate,
    order_date: f.orderDate,
    flow_metadata: f.flowMetadata,
    created_at: f.createdAt,
    updated_at: f.updatedAt,
    deleted_at: f.deletedAt,
  };
}
